import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Sparkles } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { supabase } from '../../lib/supabaseClient'

// MomentsDigestPage — /moments/digest/:yearMonth
//
// Read-only view of a month's AI-generated retrospective. The digest
// row is written by the moments-monthly-digest edge function on the
// 1st of each month; this page renders whichever locale is active
// and falls back to the other if the active one is missing.
export function MomentsDigestPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { yearMonth } = useParams()
  const [digest, setDigest] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user || !yearMonth) return undefined
    let alive = true
    ;(async () => {
      setLoading(true)
      const { data } = await supabase
        .from('moments_digests')
        .select('id, year_month, prompt_count, on_time_count, longest_streak, narrative_en, narrative_ar, generated_at')
        .eq('year_month', yearMonth)
        .maybeSingle()
      if (alive) {
        setDigest(data || null)
        setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [user, yearMonth])

  if (!authLoading && user && !hasOwnerAccess(user)) {
    return <Navigate to="/" replace />
  }
  if (authLoading || loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin opacity-60" />
      </div>
    )
  }

  // Pretty title — "May 2026" / "مايو 2026"
  let prettyMonth = yearMonth
  try {
    const [y, m] = (yearMonth || '').split('-')
    const d = new Date(Number(y), Number(m) - 1, 1)
    prettyMonth = d.toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-US', {
      month: 'long', year: 'numeric',
    })
  } catch { /* keep raw */ }

  const narrative = digest
    ? (locale === 'ar' ? (digest.narrative_ar || digest.narrative_en) : (digest.narrative_en || digest.narrative_ar))
    : null
  const paragraphs = (narrative || '').split(/\n{2,}/).filter((p) => p.trim().length > 0)

  return (
    <div className="mx-auto max-w-md space-y-5 p-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/moments/archive')}
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label={t('moments.back')}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-rose-500" />
          <h1 dir="auto" className="text-lg font-semibold">{prettyMonth}</h1>
        </div>
      </div>

      {!digest ? (
        <p dir="auto" className="pt-8 text-center text-sm opacity-70">{t('moments.digestNotFound')}</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Stat label={t('moments.digestStats.moments')} value={digest.on_time_count} />
            <Stat label={t('moments.digestStats.longestStreak')} value={digest.longest_streak} />
            <Stat label={t('moments.digestStats.totalDays')} value={digest.prompt_count} />
          </div>
          {paragraphs.length > 0 ? (
            <div className="space-y-3 rounded-2xl bg-rose-500/5 px-4 py-4">
              {paragraphs.map((p, i) => (
                <p key={i} dir="auto" className="text-sm leading-relaxed">{p}</p>
              ))}
            </div>
          ) : (
            <p dir="auto" className="text-center text-sm opacity-70">{t('moments.digestEmpty')}</p>
          )}
        </>
      )}
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-neutral-100 px-2 py-3 text-center dark:bg-neutral-800">
      <div className="text-xl font-bold leading-none">{value}</div>
      <div dir="auto" className="mt-1 text-[10px] uppercase tracking-wide opacity-60">{label}</div>
    </div>
  )
}
